import * as React from 'react';
import {
  Box, Typography, TextField, Button, Checkbox, FormControlLabel,
  Select, MenuItem, FormControl, InputLabel, Grid, Paper, Chip,
  InputAdornment, Snackbar, Alert, IconButton, Radio, Divider,
} from '@mui/material';
import VideocamIcon from '@mui/icons-material/Videocam';
import SensorsIcon from '@mui/icons-material/Sensors';
import LockIcon from '@mui/icons-material/Lock';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import DeleteConfirmDialog from '../components/DeleteConfirmDialog';
import { fetchSolutions, createSolution, updateSolution, deleteSolution } from '../services/ApiService';

// ─── Constants ────────────────────────────────────────────────────────────────

const DEVICE_TYPES = [
  { value: 'AFDD',     label: 'AFDD',     subtitle: 'Advanced Fleet Digital Device', Icon: VideocamIcon, color: '#1a237e' },
  { value: 'Sentinel', label: 'Sentinel', subtitle: 'Smart Monitoring Device',        Icon: SensorsIcon,  color: '#1b5e20' },
  { value: 'E-Seal',   label: 'E-Seal',   subtitle: 'Electronic Seal Device',         Icon: LockIcon,     color: '#4a148c' },
];

const MEMORY_TYPES      = ['SD Card', 'SSD', 'HDD'];
const MEMORY_CAPACITIES = ['32 GB', '64 GB', '128 GB', '256 GB', '512 GB', '1 TB'];
const CHANNEL_OPTIONS   = ['Front', 'Cabin', 'Rear', 'API', 'Manhole', 'Right', 'Left'];
const FEATURE_OPTIONS   = ['ADAS', 'DMS', 'GPS', 'Wi-Fi', '4G', 'Driver ID', 'Temperature Sensor', 'Fuel Sensor', 'Panic Button'];

const CHANNEL_COLORS = {
  Front: '#1565c0', Cabin: '#6a1b9a', Rear: '#2e7d32',
  API: '#e65100', Manhole: '#b71c1c', Right: '#006064', Left: '#4527a0',
};

const BLANK_FORM = {
  name: '', device_type: 'AFDD',
  memory_type: 'SD Card', memory_capacity: '128 GB',
  camera_channels: [], no_of_chambers: '',
  battery_capacity: '', additional_features: [],
  cost: '', transfer_cost: '', remarks: '',
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function dtMeta(deviceType) {
  return DEVICE_TYPES.find(d => d.value === deviceType) || DEVICE_TYPES[0];
}

function fmtCost(val) {
  if (!val && val !== 0) return 'Custom';
  const n = Number(val);
  return isNaN(n) ? 'Custom' : `${n.toLocaleString()} PKR`;
}

// ─── Main Component ───────────────────────────────────────────────────────────

export function Device() {
  const [solutions, setSolutions]         = React.useState([]);
  const [formData, setFormData]           = React.useState({ ...BLANK_FORM });
  const [editId, setEditId]               = React.useState(null);
  const [formErrors, setFormErrors]       = React.useState({});
  const [snackbar, setSnackbar]           = React.useState({ open: false, message: '', severity: 'success' });
  const [deleteConfirmOpen, setDeleteConfirmOpen] = React.useState(false);
  const [deleteTargetId, setDeleteTargetId]       = React.useState(null);

  const loadSolutions = async () => {
    try {
      const res = await fetchSolutions();
      setSolutions(res.data);
    } catch {
      showSnackbar('Failed to load solutions', 'error');
    }
  };

  React.useEffect(() => { loadSolutions(); }, []);

  const showSnackbar = (message, severity) => setSnackbar({ open: true, message, severity });
  const closeSnackbar = () => setSnackbar(s => ({ ...s, open: false }));

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(p => ({ ...p, [name]: value }));
    setFormErrors(p => ({ ...p, [name]: null }));
  };

  const toggleList = (field, value) =>
    setFormData(p => ({
      ...p,
      [field]: p[field].includes(value) ? p[field].filter(v => v !== value) : [...p[field], value],
    }));

  const selectDeviceType = (dt) =>
    setFormData(p => ({ ...p, device_type: dt, camera_channels: [], additional_features: [] }));

  const handleCancel = () => { setFormData({ ...BLANK_FORM }); setEditId(null); setFormErrors({}); };

  const handleEdit = (sol) => {
    setEditId(sol.id);
    setFormData({
      name:                sol.name || '',
      device_type:         sol.device_type || 'AFDD',
      memory_type:         sol.memory_type || 'SD Card',
      memory_capacity:     sol.memory_capacity || '128 GB',
      camera_channels:     sol.camera_channels || [],
      no_of_chambers:      sol.no_of_chambers ?? '',
      battery_capacity:    sol.battery_capacity || '',
      additional_features: sol.additional_features || [],
      cost:                sol.cost || '',
      transfer_cost:       sol.transfer_cost || '',
      remarks:             sol.remarks || '',
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      ...formData,
      no_of_chambers: formData.no_of_chambers === '' ? null : parseInt(formData.no_of_chambers, 10),
    };
    try {
      if (editId) {
        await updateSolution(editId, payload);
        showSnackbar('Solution updated successfully', 'success');
      } else {
        await createSolution(payload);
        showSnackbar('Solution saved successfully', 'success');
      }
      handleCancel();
      loadSolutions();
    } catch (error) {
      if (error.response?.data) setFormErrors(error.response.data);
      else showSnackbar('Something went wrong', 'error');
    }
  };

  const handleDeleteClick = (id) => { setDeleteTargetId(id); setDeleteConfirmOpen(true); };
  const confirmDelete = async () => {
    if (deleteTargetId) {
      try {
        await deleteSolution(deleteTargetId);
        showSnackbar('Solution deleted', 'success');
        if (editId === deleteTargetId) handleCancel();
        loadSolutions();
      } catch { showSnackbar('Failed to delete', 'error'); }
    }
    setDeleteConfirmOpen(false);
    setDeleteTargetId(null);
  };

  const isEseal = formData.device_type === 'E-Seal';

  return (
    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3, p: { xs: 2, md: 3 }, alignItems: 'flex-start', minHeight: '100vh', bgcolor: '#f5f7fa' }}>

      {/* ── LEFT : Form ─────────────────────────────────────────────────────── */}
      <Box sx={{ flex: '1 1 60%', maxWidth: { xs: '100%', md: 720 }, width: '100%' }}>
        <Paper elevation={1} sx={{ p: 4, borderRadius: 3 }}>
          <Typography variant="h5" fontWeight={700} mb={3}>
            {editId ? 'Edit Solution' : 'Add New Solution'}
          </Typography>

          <form onSubmit={handleSubmit}>

            {/* Device Name */}
            <TextField
              fullWidth required label="Device Name" name="name"
              value={formData.name} onChange={handleChange}
              error={!!formErrors.name} helperText={formErrors.name}
              sx={{ mb: 3 }}
            />

            {/* Device Type Cards */}
            <Typography variant="subtitle2" fontWeight={600} color="text.secondary" mb={1.5}>
              Select Device Type *
            </Typography>
            <Grid container spacing={2} mb={3}>
              {DEVICE_TYPES.map(({ value, label, subtitle, Icon, color }) => {
                const selected = formData.device_type === value;
                return (
                  <Grid item xs={12} sm={4} key={value}>
                    <Paper
                      onClick={() => selectDeviceType(value)}
                      elevation={selected ? 4 : 1}
                      sx={{
                        p: 2, cursor: 'pointer', borderRadius: 2, position: 'relative',
                        border: selected ? '2px solid #1976d2' : '2px solid transparent',
                        bgcolor: selected ? '#e3f2fd' : '#fff',
                        transition: 'all 0.18s',
                        '&:hover': { boxShadow: 4 },
                      }}
                    >
                      <Radio checked={selected} size="small" disableRipple
                        sx={{ position: 'absolute', top: 6, left: 6, p: 0 }} />
                      <Box sx={{
                        width: '100%', height: 88, bgcolor: color, borderRadius: 1.5,
                        display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1.5, mt: 0.5,
                      }}>
                        <Icon sx={{ fontSize: 44, color: '#fff' }} />
                      </Box>
                      <Typography fontWeight={700} fontSize="0.95rem">{label}</Typography>
                      <Typography variant="caption" color="text.secondary">{subtitle}</Typography>
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>

            {/* Memory — AFDD / Sentinel only */}
            {!isEseal && (
              <Box mb={3}>
                <Typography variant="subtitle2" fontWeight={600} color="text.secondary" mb={1.5}>
                  Memory *
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <FormControl fullWidth>
                      <InputLabel>Select Memory Type</InputLabel>
                      <Select name="memory_type" value={formData.memory_type} onChange={handleChange} label="Select Memory Type">
                        {MEMORY_TYPES.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={6}>
                    <FormControl fullWidth required>
                      <InputLabel>Capacity</InputLabel>
                      <Select name="memory_capacity" value={formData.memory_capacity} onChange={handleChange} label="Capacity">
                        {MEMORY_CAPACITIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Box>
            )}

            {/* Camera Channels — AFDD / Sentinel only */}
            {!isEseal && (
              <Box mb={3}>
                <Typography variant="subtitle2" fontWeight={600} color="text.secondary" mb={1}>
                  No of Camera Channel *
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
                  {CHANNEL_OPTIONS.map(ch => (
                    <FormControlLabel key={ch}
                      control={
                        <Checkbox size="small"
                          checked={formData.camera_channels.includes(ch)}
                          onChange={() => toggleList('camera_channels', ch)}
                        />
                      }
                      label={ch} sx={{ mr: 1 }}
                    />
                  ))}
                </Box>
              </Box>
            )}

            {/* No. of Chambers — E-Seal only */}
            {isEseal && (
              <TextField
                fullWidth label="No. of Chambers" name="no_of_chambers" type="number"
                value={formData.no_of_chambers} onChange={handleChange}
                error={!!formErrors.no_of_chambers} helperText={formErrors.no_of_chambers}
                sx={{ mb: 3 }}
              />
            )}

            {/* External Backup Battery */}
            <Box mb={3}>
              <Typography variant="subtitle2" fontWeight={600} color="text.secondary" mb={1.5}>
                External Backup Battery
              </Typography>
              <TextField
                fullWidth label="Capacity (Ampere/Hours)" name="battery_capacity"
                value={formData.battery_capacity} onChange={handleChange}
                placeholder="e.g. 12 Ah"
                error={!!formErrors.battery_capacity} helperText={formErrors.battery_capacity}
              />
            </Box>

            {/* Additional Features — AFDD / Sentinel only */}
            {!isEseal && (
              <Box mb={3}>
                <Typography variant="subtitle2" fontWeight={600} color="text.secondary" mb={1}>
                  Additional Features
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
                  {FEATURE_OPTIONS.map(f => (
                    <FormControlLabel key={f}
                      control={
                        <Checkbox size="small"
                          checked={formData.additional_features.includes(f)}
                          onChange={() => toggleList('additional_features', f)}
                        />
                      }
                      label={f} sx={{ mr: 1 }}
                    />
                  ))}
                </Box>
              </Box>
            )}

            {/* Cost + Transfer Cost */}
            <Grid container spacing={2} mb={3}>
              <Grid item xs={6}>
                <TextField
                  fullWidth required label="Cost" name="cost"
                  value={formData.cost} onChange={handleChange}
                  InputProps={{ endAdornment: <InputAdornment position="end">PKR</InputAdornment> }}
                  error={!!formErrors.cost} helperText={formErrors.cost}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth label="Transfer Cost" name="transfer_cost"
                  value={formData.transfer_cost} onChange={handleChange}
                  InputProps={{ endAdornment: <InputAdornment position="end">PKR</InputAdornment> }}
                  error={!!formErrors.transfer_cost} helperText={formErrors.transfer_cost}
                />
              </Grid>
            </Grid>

            {/* Remarks */}
            <TextField
              fullWidth multiline rows={3} label="Remarks" name="remarks"
              value={formData.remarks} onChange={handleChange}
              sx={{ mb: 3 }}
            />

            {/* Action Buttons */}
            <Box display="flex" gap={2} justifyContent="flex-end">
              <Button variant="outlined" onClick={handleCancel}>Cancel</Button>
              <Button variant="contained" type="submit">
                {editId ? 'Update Solution' : 'Save Solution'}
              </Button>
            </Box>

          </form>
        </Paper>
      </Box>

      {/* ── RIGHT : Device Summary ───────────────────────────────────────────── */}
      <Box sx={{ flex: '1 1 35%', maxWidth: { xs: '100%', md: 420 }, width: '100%', position: { xs: 'static', md: 'sticky' }, top: 16 }}>
        <Paper elevation={1} sx={{ p: 3, borderRadius: 3 }}>
          <Typography variant="h6" fontWeight={700} mb={2}>Device Summary</Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxHeight: 'calc(100vh - 160px)', overflowY: 'auto', pr: 0.5 }}>

            {solutions.length === 0 && (
              <Box sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>
                <Typography variant="body2">No solutions added yet.</Typography>
              </Box>
            )}

            {solutions.map(sol => {
              const { Icon, color } = dtMeta(sol.device_type);
              const editing = editId === sol.id;
              return (
                <Paper key={sol.id} elevation={editing ? 3 : 1} sx={{
                  p: 2, borderRadius: 2,
                  border: editing ? '2px solid #1976d2' : '1px solid #e0e0e0',
                  bgcolor: editing ? '#e3f2fd' : '#fff',
                }}>
                  {/* Header */}
                  <Box display="flex" alignItems="center" gap={1.5} mb={1}>
                    <Box sx={{
                      width: 52, height: 52, bgcolor: color, borderRadius: 1.5, flexShrink: 0,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <Icon sx={{ fontSize: 26, color: '#fff' }} />
                    </Box>
                    <Box flex={1} minWidth={0}>
                      <Typography fontWeight={700} fontSize="0.95rem" noWrap>{sol.name}</Typography>
                      <Typography variant="caption" color="text.secondary">{sol.device_type}</Typography>
                    </Box>
                    {editing && <CheckCircleIcon color="primary" fontSize="small" />}
                    <IconButton size="small" onClick={() => handleEdit(sol)} sx={{ p: 0.5 }}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => handleDeleteClick(sol.id)} sx={{ p: 0.5 }}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>

                  <Divider sx={{ mb: 1 }} />

                  {/* Details */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6 }}>

                    {sol.memory_type && (
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="caption" color="text.secondary">Memory</Typography>
                        <Typography variant="caption" fontWeight={500}>
                          {sol.memory_type} ({sol.memory_capacity})
                        </Typography>
                      </Box>
                    )}

                    {sol.camera_channels?.length > 0 && (
                      <Box>
                        <Box display="flex" justifyContent="space-between" mb={0.5}>
                          <Typography variant="caption" color="text.secondary">No. of Camera Channel</Typography>
                          <Typography variant="caption" fontWeight={600}>{sol.camera_channels.length}</Typography>
                        </Box>
                        <Box display="flex" flexWrap="wrap" gap={0.5}>
                          {sol.camera_channels.map(ch => (
                            <Chip key={ch} label={ch} size="small"
                              sx={{ height: 18, fontSize: '0.65rem', bgcolor: CHANNEL_COLORS[ch] || '#607d8b', color: '#fff', '& .MuiChip-label': { px: 0.8 } }}
                            />
                          ))}
                        </Box>
                      </Box>
                    )}

                    {sol.no_of_chambers != null && sol.no_of_chambers !== '' && (
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="caption" color="text.secondary">No. of Chambers</Typography>
                        <Typography variant="caption" fontWeight={500}>{sol.no_of_chambers}</Typography>
                      </Box>
                    )}

                    {sol.battery_capacity && (
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="caption" color="text.secondary">External Backup Battery</Typography>
                        <Typography variant="caption" fontWeight={500}>{sol.battery_capacity}</Typography>
                      </Box>
                    )}

                    {sol.additional_features?.length > 0 && (
                      <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                        <Typography variant="caption" color="text.secondary">Additional Features</Typography>
                        <Typography variant="caption" fontWeight={500} textAlign="right" maxWidth="55%">
                          {sol.additional_features.join(', ')}
                        </Typography>
                      </Box>
                    )}

                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="caption" color="text.secondary">Cost</Typography>
                      <Typography variant="caption" fontWeight={500}>{fmtCost(sol.cost)}</Typography>
                    </Box>

                    {sol.transfer_cost && (
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="caption" color="text.secondary">Transfer Cost</Typography>
                        <Typography variant="caption" fontWeight={500}>{fmtCost(sol.transfer_cost)}</Typography>
                      </Box>
                    )}

                  </Box>
                </Paper>
              );
            })}
          </Box>

          {/* Hint */}
          <Box sx={{ mt: 2, p: 1.5, bgcolor: '#e3f2fd', borderRadius: 1.5, display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            <InfoOutlinedIcon sx={{ fontSize: 15, color: '#1565c0', mt: 0.15 }} />
            <Typography variant="caption" color="#1565c0" lineHeight={1.5}>
              Select a device type to configure its specific details.
              Fields will change based on the selected device type.
            </Typography>
          </Box>
        </Paper>
      </Box>

      {/* ── Dialogs & Snackbar ───────────────────────────────────────────────── */}
      <DeleteConfirmDialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        onConfirm={confirmDelete}
      />
      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={closeSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert onClose={closeSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

    </Box>
  );
}
